import { HStack, PageContainer, PageHeader, Text, VStack } from '@/components/Atoms'
import { Block } from '@/components/Block'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/Card'
import { CustomerId } from '@/components/DataTable/CellContent/CustomerId'
import NoDataAvailable from '@/components/DataTable/CellContent/NoDataAvailable'
import { TableHeader } from '@/components/DataTable/CellContent/TableHeader'
import { ClientDataTable } from '@/components/DataTable/ClientDataTable'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { NewDropdown } from '@/components/NewDropdown'
import { PendingChanges } from '@/components/PendingChanges'
import { Spinner } from '@/components/Spinner'
import TargetAll from '@/components/DataTable/CellContent/TargetAll'
import { useBudgetAllocationOptions } from '@/hooks/queries/crt/useBudgetAllocationCodes'
import {
  ManualInvestmentChanges,
  manualInvestmentByIdQueryOptions,
  useManualInvestmentById,
  useManualInvestmentConditionTypes,
  useSaveManualInvestmentChanges,
} from '@/hooks/queries/crt/useManualInvestments'
import { useIntlLocale } from '@/hooks/useIntlLocale'
import { useLocalizedDate } from '@/hooks/useLocalizedDate'
import { formatUppercaseToTitleCase } from '@/lib/utils/strings'
import { queryClient } from '@/main'
import {
  EnrichedProductTarget,
  HydratedCustomerTarget,
  UpdateManualInvestment,
} from '@repo/backend/crt'
import {
  MANUAL_INVESTMENT_ACCOUNT_CATEGORIES,
  MONTH_KEYS,
  ManualInvestmentAccountCategory,
  MonthKey,
} from '@repo/constants'
import {
  Button,
  Field,
  FieldError,
  FieldLabel,
  Input,
  InputCurrency,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/ui/components'
import { createFileRoute } from '@tanstack/react-router'
import { ColumnDef } from '@tanstack/react-table'
import { AlertTriangleIcon, CheckIcon, Pencil, Trash2Icon, Undo2Icon, XIcon } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { AmountTypeSelect } from './-components/AmountTypeSelect'
import { CreateCustomerTargetDialog } from './-components/CreateCustomerTargetDialog'
import { CreateProductTargetDialog } from './-components/CreateProductTargetDialog'
import {
  buildBudgetBucketOptions,
  buildVistexConditionTypeOptions,
} from './-components/investmentDropdownOptions'
import { MarkingTargetsCard } from './-components/MarkingTargetsCard'
import { TargetsCard } from './-components/TargetsCard'

export const Route = createFileRoute(
  '/_auth/_modules/crt/customer-investments/insights/manual-investments/$manualInvestmentId',
)({
  component: RouteComponent,
  loader: async ({ params: { manualInvestmentId } }) => {
    const manualInvestment = await queryClient.ensureQueryData(
      manualInvestmentByIdQueryOptions(manualInvestmentId),
    )

    return {
      crumb: manualInvestment.programName,
    }
  },
})

// Rows in the target tables are either persisted (have a real `id`) or staged locally
// (have a `tempKey`). `isUnsaved` is what ClientDataTable reads to flag pending rows;
// `isPendingDelete` distinguishes "staged for deletion" from "newly added" so the cells
// can be rendered with strikethrough rather than hidden.
type CustomerTargetRow = {
  id?: number
  tempKey?: string
  isUnsaved?: boolean
  isPendingDelete?: boolean
  isAllRow?: boolean
  accountCategory: string | null
  hierarchyErp: string | null
  sapErp: string | null
  accountType: string | null
  legalName: string | null
}

type ProductTargetRow = {
  id?: number
  tempKey?: string
  isUnsaved?: boolean
  isPendingDelete?: boolean
  isAllRow?: boolean
  productCategory: string | null
  materialGroupCode: string | null
  materialGroupEn: string | null
  effectiveMaterialGroupEn: string | null
  brand: string | null
  variant: string | null
  productType: string | null
  packContentSize: string | null
}

function StruckIfDeleting({
  isPendingDelete,
  children,
}: {
  isPendingDelete?: boolean
  children: ReactNode
}) {
  if (!isPendingDelete) return <>{children}</>
  return <span className="line-through opacity-60">{children}</span>
}

// Stable identifier for a target row (persisted id or staged tempKey). The synthetic "All" row
// has neither and is never flagged.
const rowKey = (row: { id?: number; tempKey?: string }) => String(row.id ?? row.tempKey ?? '')

// Product target dimensions, broadest → narrowest. A null dimension is a wildcard.
const PRODUCT_DIMENSIONS = [
  'productCategory',
  'materialGroupCode',
  'brand',
  'variant',
  'productType',
  'packContentSize',
] as const

const customerTargetIdentity = (target: {
  accountCategory: string | null
  hierarchyErp: string | null
}) => `${target.accountCategory ?? ''}|${target.hierarchyErp ?? ''}`

const productTargetIdentity = (target: {
  productCategory: string | null
  materialGroupCode: string | null
  brand: string | null
  variant: string | null
  productType: string | null
  packContentSize: string | null
}) => PRODUCT_DIMENSIONS.map((dimension) => target[dimension] ?? '').join('|')

const productSpecificity = (row: ProductTargetRow) =>
  PRODUCT_DIMENSIONS.filter((dimension) => row[dimension] != null).length

// `broad` covers `narrow` when, for every dimension `broad` constrains, `narrow` shares the same
// value (a null in `broad` acts as a wildcard).
const productSubsumes = (broad: ProductTargetRow, narrow: ProductTargetRow) =>
  PRODUCT_DIMENSIONS.every(
    (dimension) => broad[dimension] == null || broad[dimension] === narrow[dimension],
  )

// Keys of rows made redundant by a strictly broader product rule. Computed over the "effective"
// set (staged deletions excluded, staged additions included) so warnings track unsaved edits.
const computeRedundantProductKeys = (rows: ProductTargetRow[]) => {
  const effective = rows.filter((row) => !row.isPendingDelete && !row.isAllRow)
  const redundant = new Set<string>()
  for (const narrow of effective) {
    const isCovered = effective.some(
      (broad) =>
        rowKey(broad) !== rowKey(narrow) &&
        productSpecificity(broad) < productSpecificity(narrow) &&
        productSubsumes(broad, narrow),
    )
    if (isCovered) redundant.add(rowKey(narrow))
  }
  return redundant
}

const computeRedundantCustomerKeys = (rows: CustomerTargetRow[]) => {
  const effective = rows.filter((row) => !row.isPendingDelete && !row.isAllRow)
  const coveredCategories = new Set(
    effective
      .filter((row) => !row.hierarchyErp && row.accountCategory)
      .map((row) => row.accountCategory),
  )
  const coversAll = coveredCategories.has(
    MANUAL_INVESTMENT_ACCOUNT_CATEGORIES.ALL_COMMERCIAL_PARTNERS,
  )

  const redundant = new Set<string>()
  for (const row of effective) {
    const isCategoryOnly = !row.hierarchyErp
    if (isCategoryOnly) {
      // Only the two narrower categories can be subsumed — by "all commercial partners".
      if (
        coversAll &&
        row.accountCategory !== MANUAL_INVESTMENT_ACCOUNT_CATEGORIES.ALL_COMMERCIAL_PARTNERS
      ) {
        redundant.add(rowKey(row))
      }
      continue
    }
    const isCovered =
      coversAll ||
      (row.accountCategory != null && coveredCategories.has(row.accountCategory)) ||
      (row.accountType === 'Head Office' &&
        coveredCategories.has(MANUAL_INVESTMENT_ACCOUNT_CATEGORIES.HEAD_OFFICE))
    if (isCovered) redundant.add(rowKey(row))
  }
  return redundant
}

function RedundantRuleWarning() {
  const { t } = useTranslation('crt')
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <AlertTriangleIcon className="size-4 text-amber-500" />
          </span>
        </TooltipTrigger>
        <TooltipContent>{t('manualInvestmentTargets.redundantWarning')}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

type InvestmentFormState = {
  programName: string
  vistexConditionType: string
  budgetBucket: string
  year: string
  isVolumeBased: boolean
  amounts: Record<MonthKey, string>
}

type StagedProductTarget = EnrichedProductTarget & { tempKey: string }
type StagedCustomerTargetRow = HydratedCustomerTarget & { tempKey: string }

type AddTargetsResult = { addedCount: number; skippedCount: number }

function RouteComponent() {
  const { t } = useTranslation(['crt', 'common'])
  const { manualInvestmentId } = Route.useParams()
  const { formatMonth } = useLocalizedDate()
  const intlLocale = useIntlLocale()

  const { data, isPending, error } = useManualInvestmentById(manualInvestmentId)

  const { data: conditionTypes, isPending: isConditionTypesPending } =
    useManualInvestmentConditionTypes()
  const { data: budgetAllocationOptions, isPending: isBudgetAllocationsPending } =
    useBudgetAllocationOptions()

  const { mutate: saveChanges, isPending: isSavePending } = useSaveManualInvestmentChanges({
    manualInvestmentId: Number(manualInvestmentId),
  })

  const [isEditingName, setIsEditingName] = useState(false)
  const [localName, setLocalName] = useState('')

  const [investmentForm, setInvestmentForm] = useState<InvestmentFormState>({
    programName: '',
    vistexConditionType: '',
    budgetBucket: '',
    year: '',
    isVolumeBased: false,
    amounts: Object.fromEntries(MONTH_KEYS.map((field) => [field, ''])) as Record<MonthKey, string>,
  })

  const [pendingNewCustomerTargets, setPendingNewCustomerTargets] = useState<
    StagedCustomerTargetRow[]
  >([])
  const [pendingDeletedCustomerTargetIds, setPendingDeletedCustomerTargetIds] = useState<
    Set<number>
  >(new Set())

  const [pendingNewProductTargets, setPendingNewProductTargets] = useState<StagedProductTarget[]>(
    [],
  )
  const [pendingDeletedProductTargetIds, setPendingDeletedProductTargetIds] = useState<Set<number>>(
    new Set(),
  )

  const [pendingNewMarkings, setPendingNewMarkings] = useState<Set<string>>(new Set())
  const [pendingDeletedMarkingTargetIds, setPendingDeletedMarkingTargetIds] = useState<Set<number>>(
    new Set(),
  )

  // Re-syncing on every data change covers both initial load and post-save refetch
  // (staging clears once the server returns the new authoritative state).
  useEffect(() => {
    if (!data) return
    setInvestmentForm({
      programName: data.programName,
      vistexConditionType: data.vistexConditionType ?? '',
      budgetBucket: data.budgetBucket ?? '',
      year: String(data.year),
      isVolumeBased: data.isVolumeBased,
      amounts: Object.fromEntries(
        MONTH_KEYS.map((field) => [field, data[field] == null ? '' : String(data[field])]),
      ) as Record<MonthKey, string>,
    })
    setIsEditingName(false)
    setPendingNewCustomerTargets([])
    setPendingDeletedCustomerTargetIds(new Set())
    setPendingNewProductTargets([])
    setPendingDeletedProductTargetIds(new Set())
    setPendingNewMarkings(new Set())
    setPendingDeletedMarkingTargetIds(new Set())
  }, [data])

  const getInvestmentUpdate = (): UpdateManualInvestment | null => {
    if (!data) return null

    const changes: UpdateManualInvestment = {}
    let hasChanges = false

    if (investmentForm.programName !== data.programName) {
      changes.programName = investmentForm.programName
      hasChanges = true
    }
    if (investmentForm.vistexConditionType !== (data.vistexConditionType ?? '')) {
      changes.vistexConditionType = investmentForm.vistexConditionType
      hasChanges = true
    }
    if (investmentForm.budgetBucket !== (data.budgetBucket ?? '')) {
      changes.budgetBucket = investmentForm.budgetBucket
      hasChanges = true
    }
    if (investmentForm.year !== '' && Number(investmentForm.year) !== data.year) {
      changes.year = Number(investmentForm.year)
      hasChanges = true
    }
    if (investmentForm.isVolumeBased !== data.isVolumeBased) {
      changes.isVolumeBased = investmentForm.isVolumeBased
      hasChanges = true
    }
    for (const field of MONTH_KEYS) {
      const raw = investmentForm.amounts[field]
      const next = raw === '' ? null : Number(raw)
      const current = data[field] == null ? null : Number(data[field])
      if (next !== current) {
        changes[field] = next
        hasChanges = true
      }
    }

    return hasChanges ? changes : null
  }

  const investmentUpdate = getInvestmentUpdate()

  // vistex condition type, budget allocation and year are required and non-nullable.
  const isVistexConditionTypeInvalid = investmentForm.vistexConditionType.trim() === ''
  const isBudgetBucketInvalid = investmentForm.budgetBucket.trim() === ''
  const isYearInvalid = investmentForm.year.trim() === ''
  const hasInvalidRequiredFields =
    isVistexConditionTypeInvalid || isBudgetBucketInvalid || isYearInvalid

  const baseCustomerRows: CustomerTargetRow[] = [
    ...(data?.customerTargets ?? []).map((target) => {
      const isPendingDelete = pendingDeletedCustomerTargetIds.has(target.id)
      return {
        id: target.id,
        accountCategory: target.accountCategory,
        hierarchyErp: target.hierarchyErp,
        sapErp: target.sapErp,
        accountType: target.accountType,
        legalName: target.legalName,
        isUnsaved: isPendingDelete,
        isPendingDelete,
      }
    }),
    ...pendingNewCustomerTargets.map((target) => ({
      tempKey: target.tempKey,
      isUnsaved: true,
      accountCategory: target.accountCategory,
      hierarchyErp: target.hierarchyErp,
      sapErp: target.sapErp,
      accountType: target.accountType,
      legalName: target.legalName,
    })),
  ]

  // No customer targets ⇒ the program targets every customer (the resolved view expands to all
  // headers). Surface that with a single read-only "All" row instead of an empty table.
  const customerTargetRows: CustomerTargetRow[] =
    baseCustomerRows.length === 0
      ? [
          {
            isAllRow: true,
            accountCategory: null,
            hierarchyErp: null,
            sapErp: null,
            accountType: null,
            legalName: null,
          },
        ]
      : baseCustomerRows

  // A persisted product target with every dimension null is the "all products" marker — it is
  // managed automatically on save, never shown as an editable row.
  const isAllNullProductTarget = (target: {
    productCategory: string | null
    materialGroupCode: string | null
    brand: string | null
    variant: string | null
    productType: string | null
    packContentSize: string | null
  }) =>
    !target.productCategory &&
    !target.materialGroupCode &&
    !target.brand &&
    !target.variant &&
    !target.productType &&
    !target.packContentSize

  const persistedRealProductTargets = (data?.productTargets ?? []).filter(
    (target) => !isAllNullProductTarget(target),
  )
  const persistedAllNullProductTargetIds = (data?.productTargets ?? [])
    .filter((target) => isAllNullProductTarget(target))
    .map((target) => target.id)

  // Real (non-marker) product rows, including those staged for deletion so the user can undo.
  const baseProductRows: ProductTargetRow[] = [
    ...persistedRealProductTargets.map((target) => {
      const isPendingDelete = pendingDeletedProductTargetIds.has(target.id)
      return {
        id: target.id,
        productCategory: target.productCategory,
        materialGroupCode: target.materialGroupCode,
        materialGroupEn: target.materialGroupEn,
        effectiveMaterialGroupEn: target.effectiveMaterialGroupEn,
        brand: target.brand,
        variant: target.variant,
        productType: target.productType,
        packContentSize: target.packContentSize,
        isUnsaved: isPendingDelete,
        isPendingDelete,
      }
    }),
    ...pendingNewProductTargets.map((target) => ({
      tempKey: target.tempKey,
      isUnsaved: true,
      productCategory: target.productCategory,
      materialGroupCode: target.materialGroupCode,
      materialGroupEn: target.materialGroupEn ?? null,
      // Newly-added (unsaved) rows show the original name; the effective name appears after save.
      effectiveMaterialGroupEn: null,
      brand: target.brand,
      variant: target.variant,
      productType: target.productType,
      packContentSize: target.packContentSize,
    })),
  ]

  // Whether, after applying pending changes, any real product target remains.
  const hasEffectiveRealProductTargets =
    persistedRealProductTargets.filter((target) => !pendingDeletedProductTargetIds.has(target.id))
      .length +
      pendingNewProductTargets.length >
    0

  const productTargetRows: ProductTargetRow[] =
    baseProductRows.length === 0
      ? [
          {
            isAllRow: true,
            productCategory: null,
            materialGroupCode: null,
            materialGroupEn: null,
            effectiveMaterialGroupEn: null,
            brand: null,
            variant: null,
            productType: null,
            packContentSize: null,
          },
        ]
      : baseProductRows

  // Rows shadowed by a less strict rule (a no-op). Surfaced with a warning before the delete
  // button so users can spot rules that have no effect.
  const redundantCustomerKeys = computeRedundantCustomerKeys(customerTargetRows)
  const redundantProductKeys = computeRedundantProductKeys(productTargetRows)

  // The all-null marker is created when the program effectively targets all products and none
  // exists yet, and removed when real product targets are present. Both count as pending changes.
  const productAllMarkerNeedsPersist =
    !hasEffectiveRealProductTargets && persistedAllNullProductTargetIds.length === 0
  const productAllMarkerNeedsRemoval =
    hasEffectiveRealProductTargets && persistedAllNullProductTargetIds.length > 0

  // Note: the all-products marker create/remove (productAllMarkerNeedsPersist/Removal) is applied
  // inside handleSave but deliberately NOT counted here — otherwise every product-target-less
  // investment would show an unsaved banner on load. In practice a save is always triggered by a
  // real change (markings/customers/etc.), and the marker is reconciled as part of that save.
  const hasTargetChanges =
    pendingNewCustomerTargets.length > 0 ||
    pendingDeletedCustomerTargetIds.size > 0 ||
    pendingNewProductTargets.length > 0 ||
    pendingDeletedProductTargetIds.size > 0 ||
    pendingNewMarkings.size > 0 ||
    pendingDeletedMarkingTargetIds.size > 0

  const hasUnsavedChanges = investmentUpdate !== null || hasTargetChanges

  const handleSave = () => {
    const emptyProductTarget = {
      productCategory: null,
      materialGroupCode: null,
      materialGroupEn: null,
      brand: null,
      variant: null,
      productType: null,
      packContentSize: null,
    }
    const changes: ManualInvestmentChanges = {
      investmentUpdate,
      customerTargets: {
        // Staged targets always carry a category (the dialog requires one); narrow the nullable
        // hydrated type back to the required enum.
        toCreate: pendingNewCustomerTargets
          .filter(
            (
              t,
            ): t is StagedCustomerTargetRow & {
              accountCategory: ManualInvestmentAccountCategory
            } => t.accountCategory != null,
          )
          .map((t) => ({ accountCategory: t.accountCategory, hierarchyErp: t.hierarchyErp })),
        toDeleteIds: [...pendingDeletedCustomerTargetIds],
      },
      productTargets: {
        toCreate: [
          ...pendingNewProductTargets.map(({ tempKey: _tempKey, ...rest }) => rest),
          // Persist the "all products" marker when nothing real is targeted and none exists yet.
          ...(productAllMarkerNeedsPersist ? [emptyProductTarget] : []),
        ],
        toDeleteIds: [
          ...pendingDeletedProductTargetIds,
          // Drop any stale "all products" marker once real targets are present.
          ...(productAllMarkerNeedsRemoval ? persistedAllNullProductTargetIds : []),
        ],
      },
      markingTargets: {
        toCreateMarkings: [...pendingNewMarkings],
        toDeleteIds: [...pendingDeletedMarkingTargetIds],
      },
    }
    saveChanges(changes)
  }

  // Effective selection = persisted markings (minus pending deletes) + pending creates.
  // Built once per render and handed to the checkbox card.
  const selectedMarkings = new Set<string>()
  for (const target of data?.markingTargets ?? []) {
    if (!pendingDeletedMarkingTargetIds.has(target.id)) selectedMarkings.add(target.marking)
  }
  for (const marking of pendingNewMarkings) selectedMarkings.add(marking)

  // Drops targets that already exist (persisted minus staged deletions, plus staged additions) or
  // that repeat within the incoming batch, then stages only the genuinely new ones. Returns the
  // counts so the dialog can surface an inline "already exists" error.
  const handleAddCustomerTargets = (targets: HydratedCustomerTarget[]): AddTargetsResult => {
    const existing = new Set<string>([
      ...(data?.customerTargets ?? [])
        .filter((t) => !pendingDeletedCustomerTargetIds.has(t.id))
        .map(customerTargetIdentity),
      ...pendingNewCustomerTargets.map(customerTargetIdentity),
    ])
    const toAdd: HydratedCustomerTarget[] = []
    for (const target of targets) {
      const identity = customerTargetIdentity(target)
      if (existing.has(identity)) continue
      existing.add(identity)
      toAdd.push(target)
    }
    if (toAdd.length > 0) {
      setPendingNewCustomerTargets((prev) => [
        ...prev,
        ...toAdd.map((t) => ({ ...t, tempKey: crypto.randomUUID() })),
      ])
    }
    return { addedCount: toAdd.length, skippedCount: targets.length - toAdd.length }
  }

  const handleStageDeleteCustomerTarget = (row: CustomerTargetRow) => {
    if (row.id !== undefined) {
      setPendingDeletedCustomerTargetIds((prev) => new Set(prev).add(row.id!))
    }
  }

  // Undo unstages either a pending create (drop the staged row) or a pending delete
  // (remove the id from the delete set so the row goes back to its persisted state).
  const handleUndoCustomerTarget = (row: CustomerTargetRow) => {
    if (row.tempKey !== undefined) {
      setPendingNewCustomerTargets((prev) => prev.filter((t) => t.tempKey !== row.tempKey))
    } else if (row.id !== undefined) {
      setPendingDeletedCustomerTargetIds((prev) => {
        if (!prev.has(row.id!)) return prev
        const next = new Set(prev)
        next.delete(row.id!)
        return next
      })
    }
  }

  const handleAddProductTargets = (rows: EnrichedProductTarget[]): AddTargetsResult => {
    const existing = new Set<string>([
      ...persistedRealProductTargets
        .filter((t) => !pendingDeletedProductTargetIds.has(t.id))
        .map(productTargetIdentity),
      ...pendingNewProductTargets.map(productTargetIdentity),
    ])
    const toAdd: EnrichedProductTarget[] = []
    for (const row of rows) {
      const identity = productTargetIdentity(row)
      if (existing.has(identity)) continue
      existing.add(identity)
      toAdd.push(row)
    }
    if (toAdd.length > 0) {
      setPendingNewProductTargets((prev) => [
        ...prev,
        ...toAdd.map((r) => ({ ...r, tempKey: crypto.randomUUID() })),
      ])
    }
    return { addedCount: toAdd.length, skippedCount: rows.length - toAdd.length }
  }

  const handleStageDeleteProductTarget = (row: ProductTargetRow) => {
    if (row.id !== undefined) {
      setPendingDeletedProductTargetIds((prev) => new Set(prev).add(row.id!))
    }
  }

  const handleUndoProductTarget = (row: ProductTargetRow) => {
    if (row.tempKey !== undefined) {
      setPendingNewProductTargets((prev) => prev.filter((t) => t.tempKey !== row.tempKey))
    } else if (row.id !== undefined) {
      setPendingDeletedProductTargetIds((prev) => {
        if (!prev.has(row.id!)) return prev
        const next = new Set(prev)
        next.delete(row.id!)
        return next
      })
    }
  }

  // Toggles route the change to the correct pending bucket so that re-toggling
  // a marking simply unstages the previous change (no zombie create+delete pairs).
  const handleToggleMarking = (marking: string, isChecked: boolean) => {
    const existing = data?.markingTargets.find((t) => t.marking === marking)

    if (isChecked) {
      if (existing) {
        setPendingDeletedMarkingTargetIds((prev) => {
          if (!prev.has(existing.id)) return prev
          const next = new Set(prev)
          next.delete(existing.id)
          return next
        })
      } else {
        setPendingNewMarkings((prev) => {
          if (prev.has(marking)) return prev
          const next = new Set(prev)
          next.add(marking)
          return next
        })
      }
    } else {
      if (existing) {
        setPendingDeletedMarkingTargetIds((prev) => {
          if (prev.has(existing.id)) return prev
          const next = new Set(prev)
          next.add(existing.id)
          return next
        })
      } else {
        setPendingNewMarkings((prev) => {
          if (!prev.has(marking)) return prev
          const next = new Set(prev)
          next.delete(marking)
          return next
        })
      }
    }
  }

  const conditionTypeOptions = buildVistexConditionTypeOptions(conditionTypes)
  const budgetBucketOptions = buildBudgetBucketOptions(budgetAllocationOptions)

  const handleStartEditingName = () => {
    setLocalName(investmentForm.programName)
    setIsEditingName(true)
  }

  const handleConfirmName = () => {
    if (!localName) {
      setIsEditingName(false)
      return
    }
    setInvestmentForm((prev) => ({ ...prev, programName: localName }))
    setIsEditingName(false)
  }

  const handleCancelEditingName = () => {
    setLocalName(investmentForm.programName)
    setIsEditingName(false)
  }

  // A specific target carries a hierarchyErp (and enriched header fields); a category-only target
  // has none — its ERP/SAP/legal-name columns render "All" to mean "every header in the category".
  const isCategoryOnlyRow = (row: CustomerTargetRow) => !row.isAllRow && !row.hierarchyErp

  const getAccountCategoryLabel = (category: string) =>
    ({
      [MANUAL_INVESTMENT_ACCOUNT_CATEGORIES.ALL_COMMERCIAL_PARTNERS]: t(
        'manualInvestmentTargets.customer.category.allCommercialPartners',
      ),
      [MANUAL_INVESTMENT_ACCOUNT_CATEGORIES.KEY_ACCOUNT]: t(
        'manualInvestmentTargets.customer.category.keyAccount',
      ),
      [MANUAL_INVESTMENT_ACCOUNT_CATEGORIES.HEAD_OFFICE]: t(
        'manualInvestmentTargets.customer.category.headOffice',
      ),
    })[category] ?? category

  const customerColumns: ColumnDef<CustomerTargetRow>[] = [
    {
      id: 'accountCategory',
      accessorKey: 'accountCategory',
      header: ({ column }) => <TableHeader column={column} sortable />,
      cell: ({ row }) => {
        if (row.original.isAllRow) return <TargetAll />
        // New rows carry an accountCategory; legacy rows (none) fall back to the enriched accountType.
        const label = row.original.accountCategory
          ? getAccountCategoryLabel(row.original.accountCategory)
          : row.original.accountType
        return (
          <StruckIfDeleting isPendingDelete={row.original.isPendingDelete}>
            {label ? <Text>{label}</Text> : <NoDataAvailable />}
          </StruckIfDeleting>
        )
      },
      meta: { displayTitle: t('manualInvestmentTargets.customer.table.accountCategory') },
    },
    {
      id: 'hierarchyErp',
      accessorKey: 'hierarchyErp',
      header: ({ column }) => <TableHeader column={column} sortable />,
      cell: ({ row }) =>
        row.original.isAllRow || isCategoryOnlyRow(row.original) ? (
          <TargetAll />
        ) : (
          <StruckIfDeleting isPendingDelete={row.original.isPendingDelete}>
            <CustomerId id={row.original.hierarchyErp ?? ''} />
          </StruckIfDeleting>
        ),
      meta: { displayTitle: t('manualInvestmentTargets.customer.table.hierarchyErp') },
    },
    {
      id: 'sapErp',
      accessorKey: 'sapErp',
      header: ({ column }) => <TableHeader column={column} sortable />,
      cell: ({ row }) =>
        row.original.isAllRow || isCategoryOnlyRow(row.original) ? (
          <TargetAll />
        ) : (
          <StruckIfDeleting isPendingDelete={row.original.isPendingDelete}>
            {row.original.sapErp ? <Text>{row.original.sapErp}</Text> : <NoDataAvailable />}
          </StruckIfDeleting>
        ),
      meta: { displayTitle: t('manualInvestmentTargets.customer.table.sapErp') },
    },
    {
      id: 'legalName',
      accessorKey: 'legalName',
      header: ({ column }) => <TableHeader column={column} sortable />,
      cell: ({ row }) =>
        row.original.isAllRow || isCategoryOnlyRow(row.original) ? (
          <TargetAll />
        ) : (
          <StruckIfDeleting isPendingDelete={row.original.isPendingDelete}>
            {row.original.legalName ? (
              <Text>{formatUppercaseToTitleCase(row.original.legalName)}</Text>
            ) : (
              <NoDataAvailable />
            )}
          </StruckIfDeleting>
        ),
      meta: { displayTitle: t('manualInvestmentTargets.customer.table.legalName') },
    },
    {
      id: 'warning',
      header: () => null,
      cell: ({ row }) =>
        !row.original.isPendingDelete && redundantCustomerKeys.has(rowKey(row.original)) ? (
          <RedundantRuleWarning />
        ) : null,
      enableSorting: false,
      meta: { headerClassName: 'w-10', cellClassName: 'w-10 text-right' },
    },
    {
      id: 'actions',
      header: () => null,
      cell: ({ row }) =>
        row.original.isAllRow ? null : row.original.isUnsaved ? (
          <HStack className="justify-end">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={t('manualInvestmentTargets.undoLabel')}
              onClick={() => handleUndoCustomerTarget(row.original)}
            >
              <Undo2Icon className="text-muted-foreground size-4" />
            </Button>
          </HStack>
        ) : (
          <HStack className="justify-end">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={t('manualInvestmentTargets.deleteLabel')}
              onClick={() => handleStageDeleteCustomerTarget(row.original)}
            >
              <Trash2Icon className="text-muted-foreground size-4" />
            </Button>
          </HStack>
        ),
      enableSorting: false,
      meta: { headerClassName: 'w-16', cellClassName: 'w-16 text-right' },
    },
  ]

  const productColumns: ColumnDef<ProductTargetRow>[] = [
    {
      id: 'productCategory',
      accessorKey: 'productCategory',
      header: ({ column }) => <TableHeader column={column} sortable />,
      cell: ({ row }) => (
        <StruckIfDeleting isPendingDelete={row.original.isPendingDelete}>
          {row.original.productCategory ? (
            <Text>{row.original.productCategory}</Text>
          ) : (
            <TargetAll />
          )}
        </StruckIfDeleting>
      ),
      meta: { displayTitle: t('manualInvestmentTargets.product.table.productCategory') },
    },
    {
      id: 'materialGroup',
      accessorKey: 'materialGroupEn',
      header: ({ column }) => <TableHeader column={column} sortable />,
      cell: ({ row }) => (
        <StruckIfDeleting isPendingDelete={row.original.isPendingDelete}>
          {row.original.materialGroupEn ? (
            row.original.effectiveMaterialGroupEn ? (
              <VStack gap="none">
                <Text>{row.original.effectiveMaterialGroupEn}</Text>
                <Text className="text-muted-foreground line-through">
                  {row.original.materialGroupEn}
                </Text>
              </VStack>
            ) : (
              <Text>{row.original.materialGroupEn}</Text>
            )
          ) : (
            <TargetAll />
          )}
        </StruckIfDeleting>
      ),
      meta: { displayTitle: t('manualInvestmentTargets.product.table.materialGroup') },
    },
    {
      id: 'brand',
      accessorKey: 'brand',
      header: ({ column }) => <TableHeader column={column} sortable />,
      cell: ({ row }) => (
        <StruckIfDeleting isPendingDelete={row.original.isPendingDelete}>
          {row.original.brand ? <Text>{row.original.brand}</Text> : <TargetAll />}
        </StruckIfDeleting>
      ),
      meta: { displayTitle: t('manualInvestmentTargets.product.table.brand') },
    },
    {
      id: 'variant',
      accessorKey: 'variant',
      header: ({ column }) => <TableHeader column={column} sortable />,
      cell: ({ row }) => (
        <StruckIfDeleting isPendingDelete={row.original.isPendingDelete}>
          {row.original.variant ? <Text>{row.original.variant}</Text> : <TargetAll />}
        </StruckIfDeleting>
      ),
      meta: { displayTitle: t('manualInvestmentTargets.product.table.variant') },
    },
    {
      id: 'productType',
      accessorKey: 'productType',
      header: ({ column }) => <TableHeader column={column} sortable />,
      cell: ({ row }) => (
        <StruckIfDeleting isPendingDelete={row.original.isPendingDelete}>
          {row.original.productType ? <Text>{row.original.productType}</Text> : <TargetAll />}
        </StruckIfDeleting>
      ),
      meta: { displayTitle: t('manualInvestmentTargets.product.table.productType') },
    },
    {
      id: 'packContentSize',
      accessorKey: 'packContentSize',
      header: ({ column }) => <TableHeader column={column} sortable />,
      cell: ({ row }) => (
        <StruckIfDeleting isPendingDelete={row.original.isPendingDelete}>
          {row.original.packContentSize ? (
            <Text>{row.original.packContentSize}</Text>
          ) : (
            <TargetAll />
          )}
        </StruckIfDeleting>
      ),
      meta: { displayTitle: t('manualInvestmentTargets.product.table.packContentSize') },
    },
    {
      id: 'warning',
      header: () => null,
      cell: ({ row }) =>
        !row.original.isPendingDelete && redundantProductKeys.has(rowKey(row.original)) ? (
          <RedundantRuleWarning />
        ) : null,
      enableSorting: false,
      meta: { headerClassName: 'w-10', cellClassName: 'w-10 text-right' },
    },
    {
      id: 'actions',
      header: () => null,
      cell: ({ row }) =>
        row.original.isAllRow ? null : row.original.isUnsaved ? (
          <HStack className="justify-end">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={t('manualInvestmentTargets.undoLabel')}
              onClick={() => handleUndoProductTarget(row.original)}
            >
              <Undo2Icon className="text-muted-foreground size-4" />
            </Button>
          </HStack>
        ) : (
          <HStack className="justify-end">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={t('manualInvestmentTargets.deleteLabel')}
              onClick={() => handleStageDeleteProductTarget(row.original)}
            >
              <Trash2Icon className="text-muted-foreground size-4" />
            </Button>
          </HStack>
        ),
      enableSorting: false,
      meta: { headerClassName: 'w-16', cellClassName: 'w-16 text-right' },
    },
  ]

  if (isPending) {
    return (
      <PageContainer className="flex items-center justify-center">
        <Spinner size="lg" />
      </PageContainer>
    )
  }

  if (error || !data) {
    return (
      <PageContainer>
        <Text>{t('manualInvestmentDetails.notFound')}</Text>
      </PageContainer>
    )
  }

  return (
    <PageContainer className="pb-28">
      <Block shouldBlock={hasUnsavedChanges} />

      <HStack gap="sm" className="justify-between">
        <VStack gap="md" className="flex-1">
          {isEditingName ? (
            <HStack gap="sm">
              <Input
                value={localName}
                onChange={(e) => setLocalName(e.target.value)}
                className="bg-background w-[400px]"
                autoFocus
                onFocus={(e) => e.target.select()}
              />
              <Button type="button" onClick={handleConfirmName} size="icon-sm">
                <CheckIcon />
              </Button>
              <Button
                type="button"
                onClick={handleCancelEditingName}
                variant="secondary"
                size="icon-sm"
              >
                <XIcon />
              </Button>
            </HStack>
          ) : (
            <HStack gap="sm" className="max-w-[75%] items-center">
              <PageHeader className="max-w-full truncate text-3xl">
                {investmentForm.programName}
              </PageHeader>
              <PermissionsGuard required={['crt_manual_investments_edit']}>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleStartEditingName}
                  size="icon-sm"
                  className="text-muted-foreground shrink-0"
                >
                  <Pencil />
                </Button>
              </PermissionsGuard>
            </HStack>
          )}
        </VStack>
      </HStack>

      <HStack gap="2xl" className="items-start">
        <Field className="flex-1" data-invalid={isVistexConditionTypeInvalid}>
          <FieldLabel>{t('manualInvestmentDetails.vistexConditionType')}</FieldLabel>
          <NewDropdown
            value={investmentForm.vistexConditionType}
            options={conditionTypeOptions}
            onChange={(vistexConditionType) =>
              setInvestmentForm((prev) => ({ ...prev, vistexConditionType }))
            }
            loading={isConditionTypesPending}
            selectionMode="single"
            searchable
          />
          {isVistexConditionTypeInvalid && (
            <FieldError errors={[{ message: t('common:errors.validation.required') }]} />
          )}
        </Field>
        <Field className="flex-1" data-invalid={isBudgetBucketInvalid}>
          <FieldLabel>{t('manualInvestmentDetails.budgetBucket')}</FieldLabel>
          <NewDropdown
            value={investmentForm.budgetBucket}
            options={budgetBucketOptions}
            onChange={(budgetBucket) => setInvestmentForm((prev) => ({ ...prev, budgetBucket }))}
            loading={isBudgetAllocationsPending}
            selectionMode="single"
            searchable
          />
          {isBudgetBucketInvalid && (
            <FieldError errors={[{ message: t('common:errors.validation.required') }]} />
          )}
        </Field>
        <Field className="w-28" data-invalid={isYearInvalid}>
          <FieldLabel htmlFor="year">{t('manualInvestmentDetails.year')}</FieldLabel>
          <Input
            id="year"
            type="number"
            className="bg-background"
            value={investmentForm.year}
            onChange={(e) => setInvestmentForm((prev) => ({ ...prev, year: e.target.value }))}
            aria-invalid={isYearInvalid}
          />
          {isYearInvalid && (
            <FieldError errors={[{ message: t('common:errors.validation.required') }]} />
          )}
        </Field>
      </HStack>

      <Card>
        <CardHeader>
          <HStack className="items-center justify-between">
            <CardTitle>
              {t('currencyRates.cadColumn', {
                label: t('manualInvestmentDetails.monthlyAmounts.title'),
              })}
            </CardTitle>
            <AmountTypeSelect
              isVolumeBased={investmentForm.isVolumeBased}
              onChange={(isVolumeBased) =>
                setInvestmentForm((prev) => ({ ...prev, isVolumeBased }))
              }
              className="w-44"
            />
          </HStack>
        </CardHeader>
        <CardContent>
          <div className="grid w-full grid-cols-4 gap-3">
            {MONTH_KEYS.map((field, monthIndex) => (
              <VStack gap="xs" key={field}>
                <Text size="xs" tone="muted">
                  {formatMonth(new Date(2000, monthIndex, 1))}
                </Text>
                <div className="w-full [&>div]:flex [&>div]:w-full">
                  <InputCurrency
                    locale={intlLocale}
                    className="w-full"
                    value={investmentForm.amounts[field]}
                    onCommit={(value) =>
                      setInvestmentForm((prev) => ({
                        ...prev,
                        amounts: { ...prev.amounts, [field]: value },
                      }))
                    }
                    aria-label={formatMonth(new Date(2000, monthIndex, 1))}
                  />
                </div>
              </VStack>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <HStack className="items-center justify-between">
            <CardTitle>{t('manualInvestmentTargets.customer.title')}</CardTitle>
            <CreateCustomerTargetDialog
              manualInvestmentId={manualInvestmentId}
              onAdd={handleAddCustomerTargets}
            />
          </HStack>
        </CardHeader>
        <CardContent>
          <ClientDataTable<CustomerTargetRow, unknown>
            columns={customerColumns}
            data={customerTargetRows}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <HStack className="items-center justify-between">
            <CardTitle>{t('manualInvestmentTargets.product.title')}</CardTitle>
            <CreateProductTargetDialog
              manualInvestmentId={manualInvestmentId}
              onAdd={handleAddProductTargets}
            />
          </HStack>
        </CardHeader>
        <CardContent>
          <ClientDataTable<ProductTargetRow, unknown>
            columns={productColumns}
            data={productTargetRows}
          />
        </CardContent>
      </Card>

      <MarkingTargetsCard
        selectedMarkings={selectedMarkings}
        onToggleMarking={handleToggleMarking}
      />

      <TargetsCard manualInvestmentId={manualInvestmentId} hasUnsavedChanges={hasUnsavedChanges} />

      <PermissionsGuard required={['crt_manual_investments_edit']}>
        <PendingChanges
          areChangesPending={hasUnsavedChanges}
          isSavePending={isSavePending}
          onSaveChanges={handleSave}
          isDisabled={hasInvalidRequiredFields}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
